import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Avatar,
  Box,
  Typography,
  Checkbox,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  SvgIcon
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import TagIcon from '@mui/icons-material/Tag';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { 
  doc, 
  deleteDoc, 
  collection, 
  writeBatch, 
  getDocs, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import ContactViewDialog from './ContactViewDialog';
import SendEventDialog from './SendEventDialog';
import SendTripDialog from './SendTripDialog';
import SendSubscriptionDialog from './SendSubscriptionDialog';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
  engagementScore: number;
  lastContactDate?: Date;
  source: string;
  createdAt: Date;
  consentStatus: 'granted' | 'denied' | 'unknown';
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  importDate?: string;
  eventId?: string;
  importedEventId?: string;
}

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  organizationId: string;
}

const ContactList: React.FC<ContactListProps> = ({ contacts, loading, organizationId }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<string[]>([]);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [bulkActionMenuAnchorEl, setBulkActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<{ id: string; name: string } | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactViewDialogOpen, setContactViewDialogOpen] = useState(false);
  const [contactToView, setContactToView] = useState<Contact | null>(null);
  const [sendEventDialogOpen, setSendEventDialogOpen] = useState(false);
  const [sendTripDialogOpen, setSendTripDialogOpen] = useState(false);
  const [sendSubscriptionDialogOpen, setSendSubscriptionDialogOpen] = useState(false);
  const [contactsToInvite, setContactsToInvite] = useState<string[]>([]);
  const [filters, setFilters] = useState<{
    eventId: string;
    importedEventId: string;
    source: string;
    importDateRange: { start: string | null; end: string | null };
    eventDateRange: { start: string | null; end: string | null };
    engagementScore: number | null;
    tags: string[];
    showFilters: boolean;
  }>({
    eventId: '',
    importedEventId: '',
    source: '',
    importDateRange: { start: null, end: null },
    eventDateRange: { start: null, end: null },
    engagementScore: null,
    tags: [],
    showFilters: false
  });
  const [availableEvents, setAvailableEvents] = useState<{id: string, name: string}[]>([]);
  const [importedEvents, setImportedEvents] = useState<{id: string, name: string}[]>([]);

  // Debug contacts data
  useEffect(() => {
     if (contacts.length > 0) {
     }
  }, [contacts, loading, organizationId, page, rowsPerPage]);

  // Fetch available events for filters
  useEffect(() => {
     if (organizationId) {
        fetchEvents();
     }
  }, [organizationId]);

  // Fetch events from Firestore
  const fetchEvents = async () => {
     try {
        // Fetch organization events
        const eventsRef = collection(db, 'events');

        // Try different queries to find organization events
        let orgEventsSnapshot;
        try {
           // First try with organizationId field
           const orgEventsQuery = query(eventsRef, where('organizationId', '==', organizationId));
           orgEventsSnapshot = await getDocs(orgEventsQuery);

           // If no results, try with orgId field
           if (orgEventsSnapshot.empty) {
              const altOrgEventsQuery = query(eventsRef, where('orgId', '==', organizationId));
              orgEventsSnapshot = await getDocs(altOrgEventsQuery);
           }
        } catch (error) {
           console.error('Error in primary event query:', error);
           // Fallback: try to get all events
           orgEventsSnapshot = await getDocs(eventsRef);
        }

        // Map to simplified format, with better naming
        const orgEvents = orgEventsSnapshot.docs.map((doc) => {
           const data = doc.data();
           return {
              id: doc.id,
              name: data.eventName || data.name || data.title || `Event ${doc.id.substring(0, 4)}`,
           };
        });

        setAvailableEvents(orgEvents);

        // IMPORTANT: For imported events, we'll query all events from various collections
        // to ensure we get ALL possible imported events

        // 1. First, get all imports from events collection
        const allImportedEvents: { id: string; name: string }[] = [];

        // Check imports collection first (if it exists)
        try {
           const importsRef = collection(db, 'imports');
           const importsSnapshot = await getDocs(importsRef);

           importsSnapshot.docs.forEach((doc) => {
              const data = doc.data();
              allImportedEvents.push({
                 id: doc.id,
                 name:
                    data.eventName || data.name || data.title || `Import ${doc.id.substring(0, 4)}`,
              });
           });
        } catch (error) {}

        // 2. Try an organization-specific imports subcollection
        try {
           const orgImportsRef = collection(db, `organizations/${organizationId}/imports`);
           const orgImportsSnapshot = await getDocs(orgImportsRef);

           orgImportsSnapshot.docs.forEach((doc) => {
              const data = doc.data();
              allImportedEvents.push({
                 id: doc.id,
                 name:
                    data.eventName ||
                    data.name ||
                    data.title ||
                    `Org Import ${doc.id.substring(0, 4)}`,
              });
           });
        } catch (error) {}

        // 3. Look for events with import-related flags
        const importQueries = [
           // source='import'
           query(eventsRef, where('source', '==', 'import')),
           // isImported=true
           query(eventsRef, where('isImported', '==', true)),
           // type='import'
           query(eventsRef, where('type', '==', 'import')),
           // importType exists
           query(eventsRef, where('importType', '!=', null)),
        ];

        for (const importQuery of importQueries) {
           try {
              const querySnapshot = await getDocs(importQuery);
              querySnapshot.docs.forEach((doc) => {
                 // Check if this event is already in our list (avoid duplicates)
                 if (!allImportedEvents.some((e) => e.id === doc.id)) {
                    const data = doc.data();
                    allImportedEvents.push({
                       id: doc.id,
                       name:
                          data.eventName ||
                          data.name ||
                          data.title ||
                          `Flagged Import ${doc.id.substring(0, 4)}`,
                    });
                 }
              });
           } catch (error) {}
        }

        // 4. Look for 'import' in the name/description of all events
        const allEvents = orgEventsSnapshot.docs;
        allEvents.forEach((doc) => {
           const data = doc.data();
           const eventName = (data.eventName || data.name || '').toLowerCase();
           const description = (data.description || data.eventDescription || '').toLowerCase();

           if (
              (eventName.includes('import') || description.includes('import')) &&
              !allImportedEvents.some((e) => e.id === doc.id)
           ) {
              allImportedEvents.push({
                 id: doc.id,
                 name:
                    data.eventName ||
                    data.name ||
                    data.title ||
                    `Text Import ${doc.id.substring(0, 4)}`,
              });
           }
        });

        // Ensure we have some imported events to show
        // Add a fallback to make events from contacts visible in the dropdown
        if (allImportedEvents.length === 0) {
           // Extract unique imported events from contacts collection
           const uniqueContactEvents = new Map<string, { id: string; name: string }>();

           contacts.forEach((contact) => {
              if (contact.importedEventId && contact.eventName) {
                 // Use the importedEventId as the key if available
                 if (!uniqueContactEvents.has(contact.importedEventId)) {
                    uniqueContactEvents.set(contact.importedEventId, {
                       id: contact.importedEventId,
                       name: contact.eventName,
                    });
                 }
              } else if (contact.eventId && contact.eventName && contact.source === 'import') {
                 // For imported contacts, use eventId as fallback
                 if (!uniqueContactEvents.has(contact.eventId)) {
                    uniqueContactEvents.set(contact.eventId, {
                       id: contact.eventId,
                       name: contact.eventName,
                    });
                 }
              }
           });

           // Add these events to the allImportedEvents array
           uniqueContactEvents.forEach((event) => {
              allImportedEvents.push(event);
           });
        }

        // Debug log all found import events

        // Only set imported events if we found some
        if (allImportedEvents.length > 0) {
           setImportedEvents(allImportedEvents);
        } else {
           setImportedEvents([]);
        }
     } catch (error) {
        console.error('Error fetching events for filters:', error);
     }
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
     setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
     setRowsPerPage(parseInt(event.target.value, 10));
     setPage(0);
  };

  // Handle selection
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
     if (event.target.checked) {
        const newSelected = contacts.map((n) => n.id);
        setSelected(newSelected);
        return;
     }
     setSelected([]);
  };

  const handleSelectClick = (event: React.MouseEvent<unknown>, id: string) => {
     const selectedIndex = selected.indexOf(id);
     let newSelected: string[] = [];

     if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
     } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
     } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
     } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
           selected.slice(0, selectedIndex),
           selected.slice(selectedIndex + 1)
        );
     }

     setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  // Action menu handlers
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, contactId: string) => {
     setActionMenuAnchorEl(event.currentTarget);
     setSelectedContactId(contactId);
  };

  const handleActionMenuClose = () => {
     setActionMenuAnchorEl(null);
     setSelectedContactId(null);
  };

  // Bulk action menu handlers
  const handleBulkActionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
     setBulkActionMenuAnchorEl(event.currentTarget);
  };

  const handleBulkActionMenuClose = () => {
     setBulkActionMenuAnchorEl(null);
  };

  // Delete contact handler
  const handleDeleteContact = async () => {
     if (!contactToDelete || !organizationId) return;

     try {
        // Delete contact from Firestore
        const contactRef = doc(
           db,
           `organizations/${organizationId}/contacts/${contactToDelete.id}`
        );
        await deleteDoc(contactRef);

        // Close dialog
        setDeleteDialogOpen(false);
        setContactToDelete(null);

        // Remove from selected list if selected
        if (selected.includes(contactToDelete.id)) {
           setSelected(selected.filter((id) => id !== contactToDelete.id));
        }

        // Trigger refresh
        onContactChanged();

        // Show success notification (if you have a notification system)
     } catch (error) {
        console.error('Error deleting contact:', error);
        // Show error notification
     }
  };

  // Open delete confirmation dialog
  const confirmDelete = (contact: Contact) => {
     setContactToDelete({
        id: contact.id,
        name: `${contact.firstName} ${contact.lastName}`.trim() || contact.email,
     });
     setDeleteDialogOpen(true);
     handleActionMenuClose();
  };

  // Bulk delete contacts
  const handleBulkDelete = async () => {
     if (selected.length === 0 || !organizationId) return;

     try {
        // Use batch write for better performance
        const batch = writeBatch(db);

        // Add each deletion to batch
        selected.forEach((id) => {
           const contactRef = doc(db, `organizations/${organizationId}/contacts/${id}`);
           batch.delete(contactRef);
        });

        // Commit batch
        await batch.commit();

        // Clear selection
        setSelected([]);

        // Trigger refresh
        onContactChanged();

        // Close menu
        handleBulkActionMenuClose();

        // Show success notification
     } catch (error) {
        console.error('Error deleting contacts:', error);
        // Show error notification
     }
  };
  
  // Callback for successful contact operations
  const onContactChanged = () => {
    setRefreshTrigger(prev => prev + 1);
    // Try to notify parent component
    if (window.dispatchEvent) {
      window.dispatchEvent(new Event('contactsUpdated'));
    }
  };
  
  // View/edit contact handler
  const handleViewContact = (contactId: string) => {
    const contactToView = contacts.find(contact => contact.id === contactId);
    if (contactToView) {
      setContactToView(contactToView);
      setContactViewDialogOpen(true);
    }
    handleActionMenuClose();
  };
  
  const handleEditContact = (contactId: string) => {
    navigate(`/organizations/${organizationId}/contacts/${contactId}/edit`);
    handleActionMenuClose();
  };
  
  // Email contact handler
  const handleEmailContact = (contactId: string) => {
    // Implement email functionality
    handleActionMenuClose();
  };
  
  // Handle sort changes
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  
  // Format date in a friendly way with EST timezone and full date/time
  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '—';
      
      // Get today's date at midnight for proper day comparison
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Convert date to same format (midnight) for comparison
      const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // For recent dates (within 7 days), show relative time and full datetime
      const diffTime = Math.abs(today.getTime() - compareDate.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Convert to Eastern Time
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      };
      
      const fullDateTime = date.toLocaleString('en-US', options) + ' EST';
      
      if (diffDays === 0) {
        // Today
        return `Today at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      } else if (diffDays === 1) {
        // Yesterday
        return `Yesterday at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      }
      
      // Full date and time for older dates
      return fullDateTime;
    } catch (e) {
      return '—';
    }
  };
  
  // Helper function to get a contact by ID
  const getContactById = (contactId: string | null): Contact => {
    if (!contactId) return {} as Contact;
    return contacts.find(contact => contact.id === contactId) || ({} as Contact);
  };

  // Filter contacts based on search query and applied filters
  const filteredContacts = contacts.filter(contact => {
    const query = searchQuery.toLowerCase();
    
    // Basic search query filter
    const matchesSearch = (
      contact.firstName.toLowerCase().includes(query) ||
      contact.lastName.toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      contact.phone?.toLowerCase().includes(query) ||
      contact.company?.toLowerCase().includes(query) ||
      contact.title?.toLowerCase().includes(query) ||
      contact.tags.some(tag => tag.toLowerCase().includes(query))
    );
    
    if (!matchesSearch) return false;
    
    // Event filter - can filter by eventId if the contact has this reference
    if (filters.eventId && contact.eventId !== filters.eventId) {
      return false;
    }
    
    // Imported event filter - can filter by importedEventId
    if (filters.importedEventId) {
      // Check multiple possible ways a contact might be associated with an event
      const matchesImportedEvent = 
        // Direct match on importedEventId
        contact.importedEventId === filters.importedEventId || 
        // Match on eventId (some contacts might have the event stored here)
        contact.eventId === filters.importedEventId ||
        // Match on eventName (for text-based matching)
        (contact.eventName && 
          importedEvents.some(event => 
            event.id === filters.importedEventId && 
            event.name === contact.eventName
          )
        );
      
      if (!matchesImportedEvent) {
        return false;
      }
    }
    
    // Source filter
    if (filters.source && contact.source !== filters.source) {
      return false;
    }
    
    // Import date range filter
    if (
      (filters.importDateRange.start || filters.importDateRange.end) && 
      contact.importDate
    ) {
      const importDate = new Date(contact.importDate);
      
      if (filters.importDateRange.start) {
        const startDate = new Date(filters.importDateRange.start);
        if (importDate < startDate) return false;
      }
      
      if (filters.importDateRange.end) {
        const endDate = new Date(filters.importDateRange.end);
        if (importDate > endDate) return false;
      }
    } else if ((filters.importDateRange.start || filters.importDateRange.end) && !contact.importDate) {
      return false;
    }
    
    // Event date range filter
    if (
      (filters.eventDateRange.start || filters.eventDateRange.end) && 
      contact.eventDate
    ) {
      const eventDate = new Date(contact.eventDate);
      
      if (filters.eventDateRange.start) {
        const startDate = new Date(filters.eventDateRange.start);
        if (eventDate < startDate) return false;
      }
      
      if (filters.eventDateRange.end) {
        const endDate = new Date(filters.eventDateRange.end);
        if (eventDate > endDate) return false;
      }
    } else if ((filters.eventDateRange.start || filters.eventDateRange.end) && !contact.eventDate) {
      return false;
    }
    
    // Engagement score filter
    if (filters.engagementScore !== null && contact.engagementScore < filters.engagementScore) {
      return false;
    }
    
    // Tags filter
    if (filters.tags.length > 0) {
      const hasMatchingTag = contact.tags.some(tag => 
        filters.tags.includes(tag)
      );
      if (!hasMatchingTag) return false;
    }
    
    return true;
  });

  // Sort contacts
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortBy) {
      case 'name':
        return (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName) * direction;
      case 'email':
        return a.email.localeCompare(b.email) * direction;
      case 'eventName':
        return (a.eventName || '').localeCompare(b.eventName || '') * direction;
      case 'eventDate': 
        // Convert dates for comparison
        const aEventDate = a.eventDate ? new Date(a.eventDate).getTime() : 0;
        const bEventDate = b.eventDate ? new Date(b.eventDate).getTime() : 0;
        return (aEventDate - bEventDate) * direction;
      case 'lastContact':
        const aDate = a.lastContactDate ? a.lastContactDate.getTime() : 0;
        const bDate = b.lastContactDate ? b.lastContactDate.getTime() : 0;
        return (aDate - bDate) * direction;
      case 'importDate':
        const aImport = a.importDate ? new Date(a.importDate).getTime() : 0;
        const bImport = b.importDate ? new Date(b.importDate).getTime() : 0;
        return (aImport - bImport) * direction;
      default:
        return 0;
    }
  });
  
  // Get engagement color
  const getEngagementColor = (score: number) => {
    if (score >= 70) return '#4caf50';
    if (score >= 40) return '#ff9800';
    return '#f44336';
  };
  
  // Calculate date difference for formatting
  const getDateDisplay = (date?: Date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString();
  };
  
  // Empty rows logic for consistent table height
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredContacts.length) : 0;

  // Send event invitation to multiple contacts
  const handleBulkSendEvent = () => {
    if (selected.length === 0) return;
    
    setContactsToInvite(selected);
    setSendEventDialogOpen(true);
    handleBulkActionMenuClose();
  };

  // Send trip invitation to multiple contacts
  const handleBulkSendTrip = () => {
    if (selected.length === 0) return;
    
    setContactsToInvite(selected);
    setSendTripDialogOpen(true);
    handleBulkActionMenuClose();
  };

  // Send subscription invitation to multiple contacts
  const handleBulkSendSubscription = () => {
    if (selected.length === 0) return;
    
    setContactsToInvite(selected);
    setSendSubscriptionDialogOpen(true);
    handleBulkActionMenuClose();
  };

  return (
    <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <TextField
          placeholder="Search contacts by name, email, phone or tag..."
          variant="outlined"
          size="small"
          fullWidth
          sx={{ maxWidth: 500, mb: 1 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button 
                  size="small" 
                  onClick={() => setFilters(prev => ({ ...prev, showFilters: !prev.showFilters }))}
                >
                  {filters.showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </InputAdornment>
            )
          }}
        />
        
        {selected.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<MoreVertIcon />}
            onClick={handleBulkActionMenuOpen}
            size="small"
          >
            Bulk Actions ({selected.length})
          </Button>
        )}
      </Box>
      
      {/* Filter Panel */}
      {filters.showFilters && (
        <Paper elevation={0} variant="outlined" sx={{ m: 2, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Advanced Filters</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              select
              label="Organization Event"
              size="small"
              value={filters.eventId}
              onChange={(e) => setFilters(prev => ({ ...prev, eventId: e.target.value }))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Organization Events</MenuItem>
              {availableEvents.length > 0 ? (
                availableEvents.map(event => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No events found</MenuItem>
              )}
            </TextField>
            
            <TextField
              select
              label="Imported Event"
              size="small"
              value={filters.importedEventId}
              onChange={(e) => setFilters(prev => ({ ...prev, importedEventId: e.target.value }))}
              sx={{ minWidth: 200 }}
              disabled={importedEvents.length === 0}
            >
              <MenuItem value="">All Imported Events</MenuItem>
              {importedEvents.length > 0 ? (
                importedEvents.map(event => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No imported events found</MenuItem>
              )}
            </TextField>
            
            <TextField
              select
              label="Source"
              size="small"
              value={filters.source}
              onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Sources</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="import">Import</MenuItem>
              <MenuItem value="ai_import">AI Import</MenuItem>
              <MenuItem value="web">Web</MenuItem>
              <MenuItem value="crm">CRM</MenuItem>
            </TextField>
            
            <TextField
              label="Min Engagement Score"
              type="number"
              size="small"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              value={filters.engagementScore === null ? '' : filters.engagementScore}
              onChange={(e) => setFilters(prev => ({ 
                ...prev, 
                engagementScore: e.target.value === '' ? null : Number(e.target.value)
              }))}
              sx={{ width: 180 }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">Import Date:</Typography>
              <TextField
                label="From"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.importDateRange.start || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  importDateRange: { ...prev.importDateRange, start: e.target.value || null }
                }))}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.importDateRange.end || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  importDateRange: { ...prev.importDateRange, end: e.target.value || null }
                }))}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2">Event Date:</Typography>
              <TextField
                label="From"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.eventDateRange.start || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  eventDateRange: { ...prev.eventDateRange, start: e.target.value || null }
                }))}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.eventDateRange.end || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  eventDateRange: { ...prev.eventDateRange, end: e.target.value || null }
                }))}
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button 
              size="small" 
              onClick={() => setFilters({
                eventId: '',
                importedEventId: '',
                source: '',
                importDateRange: { start: null, end: null },
                eventDateRange: { start: null, end: null },
                engagementScore: null,
                tags: [],
                showFilters: true
              })}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>
      )}
      
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < filteredContacts.length}
                      checked={filteredContacts.length > 0 && selected.length === filteredContacts.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('name')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Contact
                    {sortBy === 'name' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('email')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Email
                    {sortBy === 'email' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell
                    onClick={() => handleSort('eventName')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Event
                    {sortBy === 'eventName' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('eventDate')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Event Date
                    {sortBy === 'eventDate' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('lastContact')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Last Contact
                    {sortBy === 'lastContact' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell
                    onClick={() => handleSort('importDate')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Import Date
                    {sortBy === 'importDate' && (
                      <span style={{ marginLeft: 5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((contact) => {
                  const isSelected = selected.indexOf(contact.id) !== -1;
                  return (
                    <TableRow
                      hover
                      key={contact.id}
                      selected={isSelected}
                      sx={{ '&:hover': { cursor: 'pointer' } }}
                      onClick={() => handleViewContact(contact.id)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => {
                        e.stopPropagation(); // Prevent row click from triggering
                        handleSelectClick(e, contact.id);
                      }}>
                        <Checkbox
                          checked={isSelected}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {contact.firstName[0]}{contact.lastName ? contact.lastName[0] : ''}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {contact.firstName} {contact.lastName}
                            </Typography>
                            {(contact.company || contact.title) && (
                              <Typography variant="body2" color="text.secondary">
                                {contact.title}{contact.title && contact.company ? ' at ' : ''}{contact.company}
                              </Typography>
                            )}
                            {contact.phone && (
                              <Typography variant="body2" color="text.secondary">
                                {contact.phone}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {contact.email}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {contact.tags.slice(0, 2).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" />
                          ))}
                          {contact.tags.length > 2 && (
                            <Chip 
                              label={`+${contact.tags.length - 2}`} 
                              size="small" 
                              variant="outlined" 
                              color="primary"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {contact.eventName ? (
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {contact.eventName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.eventDate ? (
                          <Typography variant="body2" noWrap>
                            {formatDate(contact.eventDate)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.lastContactDate ? (
                          formatDate(contact.lastContactDate)
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={contact.source === 'ai_import' ? 'AI Import' : contact.source} 
                          size="small"
                          color={contact.source === 'ai_import' ? 'secondary' : 'default'}
                          sx={{ 
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {contact.importDate ? (
                          <Typography variant="body2">
                            {formatDate(contact.importDate)}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="more"
                          onClick={(event) => {
                            event.stopPropagation(); // Prevent row click from triggering
                            handleActionMenuOpen(event, contact.id);
                          }}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      {filteredContacts.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No contacts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add your first contact or import contacts to get started
          </Typography>
        </Box>
      )}
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredContacts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      
      {/* Contact Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => selectedContactId && handleViewContact(selectedContactId)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => selectedContactId && handleEditContact(selectedContactId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={() => selectedContactId && handleEmailContact(selectedContactId)}>
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          Send Email
        </MenuItem>
        <MenuItem onClick={() => selectedContactId && setSendEventDialogOpen(true)}>
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          Send Event
        </MenuItem>
        <MenuItem onClick={() => {}}>
          <ListItemIcon>
            <GroupAddIcon fontSize="small" />
          </ListItemIcon>
          Add to Group
        </MenuItem>
        <MenuItem onClick={() => confirmDelete(getContactById(selectedContactId))}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
      
      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionMenuAnchorEl}
        open={Boolean(bulkActionMenuAnchorEl)}
        onClose={handleBulkActionMenuClose}
      >
        <MenuItem onClick={handleBulkSendEvent}>
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          Send Event Invite
        </MenuItem>
        <MenuItem onClick={handleBulkSendTrip}>
          <ListItemIcon>
            <EventIcon fontSize="small" />
          </ListItemIcon>
          Send Trip Invite
        </MenuItem>
        <MenuItem onClick={handleBulkSendSubscription}>
          <ListItemIcon>
            <SubscriptionsIcon fontSize="small" />
          </ListItemIcon>
          Send Subscription Invite
        </MenuItem>
        <MenuItem onClick={handleBulkDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Selected
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the contact "{contactToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteContact} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Contact View Dialog */}
      <ContactViewDialog
        open={contactViewDialogOpen}
        onClose={() => setContactViewDialogOpen(false)}
        contactId={contactToView?.id || ''}
        organizationId={organizationId}
      />
      
      {/* Send Event Dialog */}
      <SendEventDialog
        open={sendEventDialogOpen}
        onClose={() => setSendEventDialogOpen(false)}
        contactsToInvite={contactsToInvite}
        organizationId={organizationId}
      />
      
      {/* Send Trip Dialog */}
      <SendTripDialog
        open={sendTripDialogOpen}
        onClose={() => setSendTripDialogOpen(false)}
        contactsToInvite={contactsToInvite}
        organizationId={organizationId}
      />
      
      {/* Send Subscription Dialog */}
      <SendSubscriptionDialog
        open={sendSubscriptionDialogOpen}
        onClose={() => setSendSubscriptionDialogOpen(false)}
        contactsToInvite={contactsToInvite}
        organizationId={organizationId}
      />
    </Paper>
  );
};

export default ContactList;
